/**
 * Storedash Wishlist
 *
 * Handles:
 * - Wishlist icon badge count (header widget)
 * - Slide-out panel open/close/refresh
 * - Product card heart buttons (add/remove)
 * - State management across all instances
 *
 * @since 3.9.0
 */
(function () {
    'use strict';

    // ─── Config from PHP ─────────────────────────────────────────────
    const config = window.storedashWishlistData || {};
    const ajaxUrl = config.ajaxUrl || '/wp-admin/admin-ajax.php';
    const nonce = config.nonce || '';
    const wishlistPageUrl = config.wishlistPageUrl || '';
    const i18n = config.i18n || {};

    // ─── State ───────────────────────────────────────────────────────
    const state = {
        productIds: new Set((config.productIds || []).map(Number)),
        count: config.initialCount || 0,
        panelOpen: false,
        loading: false,
        cachedItems: null,
        cacheStale: true,
    };

    // Sync from sessionStorage on load (fallback for same-session navigations)
    try {
        const cached = sessionStorage.getItem('storedash_wishlist_ids');
        if (cached) {
            JSON.parse(cached).forEach((id) => state.productIds.add(Number(id)));
            state.count = state.productIds.size;
        }
        const cachedItemsStr = sessionStorage.getItem('storedash_wishlist_items');
        if (cachedItemsStr) {
            const parsed = JSON.parse(cachedItemsStr);
            if (Array.isArray(parsed)) {
                state.cachedItems = parsed;
                state.cacheStale = true;
            }
        }
    } catch (e) { /* ignore */ }

    // Server-provided items take precedence over sessionStorage
    if (Array.isArray(config.initialItems)) {
        state.cachedItems = config.initialItems;
        state.cacheStale = false;
    }

    function persistState() {
        try {
            sessionStorage.setItem('storedash_wishlist_ids', JSON.stringify([...state.productIds]));
            if (state.cachedItems !== null) {
                sessionStorage.setItem('storedash_wishlist_items', JSON.stringify(state.cachedItems));
            }
        } catch (e) { /* ignore */ }
    }

    // ─── Device Detection ────────────────────────────────────────────
    function isMobileDevice() {
        return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
            (navigator.maxTouchPoints > 1 && /Macintosh/i.test(navigator.userAgent));
    }

    // ─── AJAX Helper ─────────────────────────────────────────────────
    function wishlistAjax(action, data = {}) {
        const body = new URLSearchParams({
            action: 'storedash_wishlist_' + action,
            nonce: nonce,
            ...data,
        });

        return fetch(ajaxUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body,
        })
            .then((r) => r.json())
            .then((r) => {
                if (r.success && r.data) {
                    if (r.data.product_ids) {
                        state.productIds = new Set(r.data.product_ids.map(Number));
                    }
                    if (typeof r.data.count !== 'undefined') {
                        state.count = r.data.count;
                    }
                    persistState();
                    updateAllBadges();
                    syncAllHearts();
                }
                return r;
            });
    }

    // ─── Badge Updates ───────────────────────────────────────────────
    function updateAllBadges() {
        document.querySelectorAll('.storedash-wishlist-trigger').forEach((trigger) => {
            trigger.setAttribute('data-wishlist-count', state.count);
            let badge = trigger.querySelector('.wishlist-count');

            if (state.count > 0) {
                if (!badge) {
                    badge = document.createElement('span');
                    badge.className = 'wishlist-count';
                    trigger.appendChild(badge);
                }
                badge.textContent = state.count;
                badge.style.display = '';
            } else if (badge) {
                badge.style.display = 'none';
            }
        });
    }

    // ─── Heart State Sync ────────────────────────────────────────────
    function heartAriaLabel(isActive) {
        const add = i18n.addToWishlistLabel || 'Add to wishlist';
        const remove = i18n.removeFromWishlistLabel || 'Remove from wishlist';
        return isActive ? remove : add;
    }

    function syncAllHearts() {
        document.querySelectorAll('.product-wishlist-heart').forEach((btn) => {
            const pid = Number(btn.dataset.productId);
            const active = state.productIds.has(pid);
            btn.classList.toggle('is-wishlisted', active);
            btn.setAttribute('aria-pressed', String(active));
            btn.setAttribute('aria-label', heartAriaLabel(active));
        });
    }

    function getProductNameFromContext(el) {
        // Prefer an explicit data-product-name attribute on the button itself.
        // Single-product-page widgets (e.g. WishlistButton) set this so the
        // toast message can include the product name even when there's no
        // enclosing .product-card.
        if (el && el.dataset && el.dataset.productName) {
            return el.dataset.productName.replace(/\s+/g, ' ').trim();
        }
        const card = el.closest('.product-card');
        if (!card) return '';
        const title = card.querySelector('.product-title');
        return title ? title.textContent.replace(/\s+/g, ' ').trim() : '';
    }

    function showStoredashWishlistToast(kind, productName) {
        if (typeof StoredashToast === 'undefined' || typeof StoredashToast.success !== 'function') {
            return;
        }
        const tc = window.storedashToast || {};
        const msgs = tc.messages || {};
        const raw = kind === 'added'
            ? (msgs.wishlist_added || '{product_name} added to your wishlist')
            : (msgs.wishlist_removed || '{product_name} removed from your wishlist');
        const name = productName || '';
        const text = String(raw).replace(/\{product_name\}/g, name).replace(/\s+/g, ' ').trim();
        const dur = (tc.durations && tc.durations.success) ? tc.durations.success : 3;
        const pos = tc.position || 'bottom-center';
        StoredashToast.success(text, { duration: dur, position: pos });
    }

    function showWishlistErrorToast() {
        if (typeof StoredashToast === 'undefined' || typeof StoredashToast.error !== 'function') {
            return;
        }
        const tc = window.storedashToast || {};
        const msgs = tc.messages || {};
        const dur = (tc.durations && tc.durations.error) ? tc.durations.error : 5;
        StoredashToast.error(msgs.wishlist_error || i18n.wishlistError || 'Could not update wishlist. Try again.', { duration: dur });
    }

    function rollbackHeartToggle(btn, wasInList, pid) {
        if (wasInList) {
            state.productIds.add(pid);
        } else {
            state.productIds.delete(pid);
        }
        state.count = state.productIds.size;
        btn.classList.toggle('is-wishlisted', wasInList);
        btn.setAttribute('aria-pressed', String(wasInList));
        btn.setAttribute('aria-label', heartAriaLabel(wasInList));
        updateAllBadges();
        persistState();
    }

    // ─── DOM Scraping for Optimistic Panel Updates ─────────────────
    function scrapeProductDataFromDOM(btn, pid) {
        const card = btn.closest('.product-card');
        if (card) {
            const titleEl = card.querySelector('.product-title');
            const priceEl = card.querySelector('.product-price');
            const imgEl = card.querySelector('.product-image img');
            const linkEl = card.querySelector('.product-card-link');
            return {
                product_id: pid,
                title: titleEl ? titleEl.textContent.replace(/\s+/g, ' ').trim() : '',
                price_html: priceEl ? priceEl.innerHTML : '',
                image_url: imgEl ? (imgEl.currentSrc || imgEl.src) : '',
                permalink: linkEl ? linkEl.href : '',
                in_stock: true,
                stock_status: 'instock',
            };
        }
        const singleProduct = document.querySelector('.product');
        if (singleProduct) {
            const titleEl = singleProduct.querySelector('.product_title, h1.entry-title');
            const priceEl = singleProduct.querySelector('.summary .price, p.price');
            const imgEl = singleProduct.querySelector('.woocommerce-product-gallery img, .wp-post-image');
            return {
                product_id: pid,
                title: titleEl ? titleEl.textContent.replace(/\s+/g, ' ').trim() : '',
                price_html: priceEl ? priceEl.innerHTML : '',
                image_url: imgEl ? (imgEl.currentSrc || imgEl.src) : '',
                permalink: window.location.href,
                in_stock: true,
                stock_status: 'instock',
            };
        }
        return null;
    }

    // ─── Heart Toggle Handler ────────────────────────────────────────
    // Clicks are turned into 'storedash:wishlist:toggle' via capture-phase
    // delegation (setupProductCardHeartCapture) so themes / card overlays
    // cannot steal the event before the button receives it.
    function handleHeartToggle(btn) {
        if (state.loading) return;

        const pid = Number(btn.dataset.productId);
        const wasInList = state.productIds.has(pid);
        const productName = getProductNameFromContext(btn);

        // Optimistic update
        let removedItem = null;
        if (wasInList) {
            state.productIds.delete(pid);
            state.count = Math.max(0, state.count - 1);
            if (state.cachedItems) {
                removedItem = state.cachedItems.find((i) => i.product_id === pid) || null;
                state.cachedItems = state.cachedItems.filter((i) => i.product_id !== pid);
            }
        } else {
            state.productIds.add(pid);
            state.count++;
            if (state.cachedItems !== null) {
                const scraped = scrapeProductDataFromDOM(btn, pid);
                if (scraped) {
                    state.cachedItems = [scraped, ...state.cachedItems];
                }
            }
        }

        const nowInList = !wasInList;
        btn.classList.toggle('is-wishlisted', nowInList);
        btn.setAttribute('aria-pressed', String(nowInList));
        btn.setAttribute('aria-label', heartAriaLabel(nowInList));
        btn.classList.add('wishlist-heart-animating');
        setTimeout(() => btn.classList.remove('wishlist-heart-animating'), 400);

        updateAllBadges();
        persistState();
        state.cacheStale = true;

        if (state.panelOpen) {
            panel.renderItems(state.cachedItems || []);
        }

        const action = wasInList ? 'remove' : 'add';
        wishlistAjax(action, { product_id: pid })
            .then((r) => {
                if (r && r.success) {
                    showStoredashWishlistToast(action === 'add' ? 'added' : 'removed', productName);
                    document.dispatchEvent(new CustomEvent('storedash:wishlist:updated'));
                    return;
                }
                rollbackHeartToggle(btn, wasInList, pid);
                rollbackCachedItems(wasInList, pid, removedItem);
                showWishlistErrorToast();
            })
            .catch(() => {
                rollbackHeartToggle(btn, wasInList, pid);
                rollbackCachedItems(wasInList, pid, removedItem);
                showWishlistErrorToast();
            });
    }

    function rollbackCachedItems(wasInList, pid, removedItem) {
        if (!state.cachedItems) return;
        if (wasInList && removedItem) {
            state.cachedItems.push(removedItem);
        } else if (!wasInList) {
            state.cachedItems = state.cachedItems.filter((i) => i.product_id !== pid);
        }
        persistState();
        if (state.panelOpen) {
            panel.renderItems(state.cachedItems);
        }
    }

    function bindHeart(btn) {
        if (btn._wishlistBound) return;
        btn._wishlistBound = true;
        btn.addEventListener('storedash:wishlist:toggle', function () {
            handleHeartToggle(btn);
        });
    }

    function bindAllHearts() {
        document.querySelectorAll('.product-wishlist-heart').forEach(bindHeart);
    }

    // ─── Slide-out Panel ─────────────────────────────────────────────
    const panel = {
        sidebar: null,
        overlay: null,
        content: null,
        shareUrl: null,
        shareUrlPending: false,

        /**
         * Prefetch the share URL so the share click handler can call
         * navigator.share() synchronously. The Web Share API requires
         * transient user activation — an AJAX round-trip between the click
         * and navigator.share() can expire it (iOS Safari especially),
         * making share() reject and silently fall back to clipboard.
         * The share token is stable per wishlist, so caching is safe.
         */
        prefetchShareUrl() {
            if (this.shareUrl || this.shareUrlPending) return;
            if (!this.sidebar?.querySelector('.wishlist-share-btn')) return;
            this.shareUrlPending = true;
            wishlistAjax('get_share_link').then((r) => {
                this.shareUrlPending = false;
                if (r && r.success && r.data && r.data.url) {
                    this.shareUrl = r.data.url;
                }
            }).catch(() => {
                this.shareUrlPending = false;
            });
        },

        init() {
            this.sidebar = document.querySelector('.storedash-wishlist-sidebar');
            this.overlay = document.querySelector('.storedash-wishlist-overlay');
            this.content = this.sidebar?.querySelector('.wishlist-panel-content');

            if (!this.sidebar) return;

            this.overlay?.addEventListener('click', () => this.close());
            this.sidebar.querySelector('.wishlist-panel-close')?.addEventListener('click', () => this.close());
            this.sidebar.querySelector('.wishlist-share-btn')?.addEventListener('click', () => this.share());
        },

        open() {
            if (!this.sidebar) return;
            state.panelOpen = true;
            this.sidebar.classList.add('is-open');
            this.overlay?.classList.add('is-open');
            document.body.classList.add('storedash-wishlist-panel-open');

            if (state.cachedItems !== null) {
                this.renderItems(state.cachedItems);
                if (state.cachedItems.length > 0) {
                    this.prefetchShareUrl();
                }
                if (state.cacheStale) {
                    this.fetchItems(true);
                }
            } else {
                this.content.innerHTML = '<div class="wishlist-panel-loading"><div class="loading-spinner"></div></div>';
                this.fetchItems(false);
            }
        },

        close() {
            if (!this.sidebar) return;
            state.panelOpen = false;
            this.sidebar.classList.remove('is-open');
            this.overlay?.classList.remove('is-open');
            document.body.classList.remove('storedash-wishlist-panel-open');
        },

        fetchItems(silent) {
            wishlistAjax('get').then((r) => {
                if (!r.success) {
                    if (!silent) {
                        const errorMsg = this.sidebar?.dataset.errorMessage || i18n.loadError || 'Could not load wishlist.';
                        this.content.innerHTML = '<p>' + this.escHtml(errorMsg) + '</p>';
                    }
                    return;
                }
                const items = r.data.items || [];
                const changed = !this.itemsEqual(state.cachedItems, items);
                state.cachedItems = items;
                state.cacheStale = false;
                persistState();
                if (state.panelOpen && changed) {
                    this.renderItems(items);
                }
                if (state.panelOpen && items.length > 0) {
                    this.prefetchShareUrl();
                }
            });
        },

        itemsEqual(a, b) {
            if (!a || !b) return false;
            if (a.length !== b.length) return false;
            for (let i = 0; i < a.length; i++) {
                if (a[i].product_id !== b[i].product_id) return false;
                if (a[i].price_html !== b[i].price_html) return false;
            }
            return true;
        },

        renderItems(items) {
            if (!this.content) return;

            const emptyMsg = this.sidebar?.dataset.emptyMessage || i18n.emptyWishlist || 'Your wishlist is empty.';
            const removeLabel = this.sidebar?.dataset.removeLabel || i18n.remove || 'Remove';

            if (items.length === 0) {
                this.content.innerHTML = `
                    <div class="wishlist-panel-empty">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                        </svg>
                        <p>${this.escHtml(emptyMsg)}</p>
                    </div>`;
                return;
            }

            this.content.innerHTML = '<div class="wishlist-panel-items">' +
                items.map((item) => `
                    <div class="wishlist-panel-item" data-product-id="${item.product_id}">
                        <a href="${item.permalink}" class="wishlist-panel-item-image">
                            <img src="${item.image_url}" alt="${this.escHtml(item.title)}" loading="lazy">
                        </a>
                        <div class="wishlist-panel-item-info">
                            <a href="${item.permalink}" class="wishlist-panel-item-title">${this.escHtml(item.title)}</a>
                            <div class="wishlist-panel-item-price">${item.price_html}</div>
                        </div>
                        <button class="wishlist-panel-item-remove" data-product-id="${item.product_id}" aria-label="${this.escHtml(removeLabel)}">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                `).join('') +
                '</div>';

            this.content.querySelectorAll('.wishlist-panel-item-remove').forEach((btn) => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const pid = Number(btn.dataset.productId);
                    const card = btn.closest('.wishlist-panel-item');
                    const titleEl = card?.querySelector('.wishlist-panel-item-title');
                    const titleText = titleEl ? titleEl.textContent.replace(/\s+/g, ' ').trim() : '';
                    card?.classList.add('removing');
                    state.cacheStale = true;
                    wishlistAjax('remove', { product_id: pid }).then((r) => {
                        if (!r || !r.success) {
                            card?.classList.remove('removing');
                            showWishlistErrorToast();
                            return;
                        }
                        card?.remove();
                        showStoredashWishlistToast('removed', titleText);
                        syncAllHearts();
                        if (state.cachedItems) {
                            state.cachedItems = state.cachedItems.filter((i) => i.product_id !== pid);
                            persistState();
                        }
                        if (this.content.querySelectorAll('.wishlist-panel-item').length === 0) {
                            this.renderItems([]);
                        }
                    }).catch(() => {
                        card?.classList.remove('removing');
                        showWishlistErrorToast();
                    });
                });
            });
        },

        share() {
            const copiedMsg = this.sidebar?.dataset.copiedToast || i18n.linkCopied || 'Link copied to clipboard!';
            const shareBtn = this.sidebar?.querySelector('.wishlist-share-btn');
            const shareTitle = shareBtn?.dataset.shareText || i18n.shareWishlist || document.title;

            const doShare = (url) => {
                // Mobile: prefer the device's native share sheet (matches copy-link widget behavior)
                if (isMobileDevice() && typeof navigator.share === 'function') {
                    // No toast on success — the native share sheet is its own
                    // feedback, and "copied to clipboard" would be wrong here.
                    navigator.share({ title: shareTitle, url: url }).catch((err) => {
                        // User cancelled the share sheet — not an error, do nothing
                        if (err && err.name === 'AbortError') return;
                        // Any other failure: fall back to clipboard
                        this.copyAndToast(url, copiedMsg);
                    });
                    return;
                }

                // Desktop / no native share: copy to clipboard
                this.copyAndToast(url, copiedMsg);
            };

            // Prefetched URL: call navigator.share() synchronously inside the
            // click gesture so transient activation is still valid (otherwise
            // mobile falls back to clipboard). See prefetchShareUrl().
            if (this.shareUrl) {
                doShare(this.shareUrl);
                return;
            }

            // No prefetched URL (prefetch failed or still in flight) — fetch
            // now and share best-effort. Activation may have expired by the
            // time this resolves; doShare's catch falls back to clipboard.
            wishlistAjax('get_share_link').then((r) => {
                if (!r.success || !r.data.url) return;
                this.shareUrl = r.data.url;
                doShare(r.data.url);
            });
        },

        copyAndToast(url, msg) {
            if (navigator.clipboard) {
                navigator.clipboard.writeText(url)
                    .then(() => this.showToast(msg))
                    // Safari can reject writeText outside user activation —
                    // fall back to the legacy execCommand path.
                    .catch(() => this.legacyCopy(url, msg));
                return;
            }
            this.legacyCopy(url, msg);
        },

        legacyCopy(url, msg) {
            const input = document.createElement('input');
            input.value = url;
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            document.body.removeChild(input);
            this.showToast(msg);
        },

        showToast(msg) {
            // Prefer the global toast system so wishlist feedback matches cart
            // actions (same position, same styling, same stacking behavior).
            if (window.StoredashToast && typeof window.StoredashToast.success === 'function') {
                window.StoredashToast.success(msg);
                return;
            }
            // Fallback: legacy inline toast (kept for safety if toast.js is absent)
            const toast = document.createElement('div');
            toast.className = 'storedash-wishlist-toast';
            toast.textContent = msg;
            document.body.appendChild(toast);
            requestAnimationFrame(() => toast.classList.add('is-visible'));
            setTimeout(() => {
                toast.classList.remove('is-visible');
                setTimeout(() => toast.remove(), 300);
            }, 2500);
        },

        escHtml(str) {
            const div = document.createElement('div');
            div.textContent = str;
            return div.innerHTML;
        },
    };

    /**
     * Capture-phase handler: product cards use overlays and themes often add
     * delegated click handlers. Running first on document guarantees the heart
     * wins without relying on hit-testing order or inline onclick.
     */
    function resolveWishlistHeartFromEvent(e) {
        const direct = e.target.closest('.product-wishlist-heart');
        if (direct) {
            return direct;
        }
        let stack;
        try {
            stack = document.elementsFromPoint(e.clientX, e.clientY);
        } catch (err) {
            return null;
        }
        const top = stack[0];
        if (!top || typeof top.closest !== 'function') {
            return null;
        }
        const h = top.closest('.product-wishlist-heart');
        if (h && h.closest('.storedash-products-wrapper, .storedash-wishlist-button')) {
            return h;
        }
        return null;
    }

    function setupProductCardHeartCapture() {
        document.addEventListener(
            'click',
            function productCardHeartCapture(e) {
                const heart = resolveWishlistHeartFromEvent(e);
                if (!heart) {
                    return;
                }
                // Allow hearts inside either a product grid (cards/overlays)
                // OR a standalone Wishlist Button widget (single product pages).
                if (!heart.closest('.storedash-products-wrapper, .storedash-wishlist-button')) {
                    return;
                }
                e.preventDefault();
                e.stopPropagation();
                // Lazy bind: widgets rendered after DOMContentLoaded (e.g. the
                // Elementor editor re-rendering a template) won't have been
                // caught by bindAllHearts() at init. bindHeart is idempotent
                // via the _wishlistBound flag, so this is safe for any heart.
                bindHeart(heart);
                heart.dispatchEvent(new CustomEvent('storedash:wishlist:toggle', { bubbles: false }));
            },
            true
        );
    }

    /**
     * Remove button on Product Grid when query type = wishlist (wishlist page layout).
     */
    function setupWishlistGridRemove() {
        document.addEventListener(
            'click',
            function wishlistGridRemoveCapture(e) {
                const btn = e.target.closest('.storedash-wishlist-remove-btn');
                if (!btn) {
                    return;
                }
                if (!btn.closest('.storedash-products-wrapper[data-wishlist-query="1"]')) {
                    return;
                }
                e.preventDefault();
                e.stopPropagation();
                const pid = Number(btn.dataset.productId);
                const card = btn.closest('.product-card');
                const name = getProductNameFromContext(btn);
                card?.classList.add('removing');
                wishlistAjax('remove', { product_id: pid })
                    .then((r) => {
                        if (r && r.success) {
                            card?.remove();
                            showStoredashWishlistToast('removed', name);
                            document.dispatchEvent(new CustomEvent('storedash:wishlist:updated'));
                            document.dispatchEvent(new CustomEvent('storedash:products:rendered'));
                            return;
                        }
                        card?.classList.remove('removing');
                        showWishlistErrorToast();
                    })
                    .catch(() => {
                        card?.classList.remove('removing');
                        showWishlistErrorToast();
                    });
            },
            true
        );
    }

    // ─── Elementor Editor ───────────────────────────────────────────

    let _editorHandled = false;

    function isElementorEditor() {
        return typeof elementorFrontend !== 'undefined' && elementorFrontend.isEditMode();
    }

    function handleElementorEditor() {
        const sidebar = document.querySelector('.storedash-wishlist-sidebar');
        const overlay = document.querySelector('.storedash-wishlist-overlay');
        if (!sidebar) return;

        // Inject sample wishlist items for editor styling preview
        const content = sidebar.querySelector('.wishlist-panel-content');
        if (content) {
            const placeholderImg = 'data:image/svg+xml,' + encodeURIComponent(
                '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60">' +
                '<rect width="60" height="60" fill="#e5e7eb"/>' +
                '<text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="9" fill="#9ca3af">Mynd</text>' +
                '</svg>'
            );
            content.innerHTML = '<div class="wishlist-panel-items">' +
                '<div class="wishlist-panel-item" data-product-id="1">' +
                    '<a href="#" class="wishlist-panel-item-image"><img src="' + placeholderImg + '" alt="Demo"></a>' +
                    '<div class="wishlist-panel-item-info">' +
                        '<a href="#" class="wishlist-panel-item-title">S\u00fdnishornsvara 1</a>' +
                        '<div class="wishlist-panel-item-price">12.990 kr.</div>' +
                    '</div>' +
                    '<button class="wishlist-panel-item-remove" data-product-id="1">' +
                        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>' +
                    '</button>' +
                '</div>' +
                '<div class="wishlist-panel-item" data-product-id="2">' +
                    '<a href="#" class="wishlist-panel-item-image"><img src="' + placeholderImg + '" alt="Demo"></a>' +
                    '<div class="wishlist-panel-item-info">' +
                        '<a href="#" class="wishlist-panel-item-title">S\u00fdnishornsvara 2</a>' +
                        '<div class="wishlist-panel-item-price">7.490 kr.</div>' +
                    '</div>' +
                    '<button class="wishlist-panel-item-remove" data-product-id="2">' +
                        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>' +
                    '</button>' +
                '</div>' +
                '<div class="wishlist-panel-item" data-product-id="3">' +
                    '<a href="#" class="wishlist-panel-item-image"><img src="' + placeholderImg + '" alt="Demo"></a>' +
                    '<div class="wishlist-panel-item-info">' +
                        '<a href="#" class="wishlist-panel-item-title">S\u00fdnishornsvara 3</a>' +
                        '<div class="wishlist-panel-item-price">4.990 kr.</div>' +
                    '</div>' +
                    '<button class="wishlist-panel-item-remove" data-product-id="3">' +
                        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>' +
                    '</button>' +
                '</div>' +
            '</div>';
        }

        const showEditorPanel = () => {
            sidebar.classList.add('is-open');
            overlay?.classList.add('is-open');
        };
        const hideEditorPanel = () => {
            sidebar.classList.remove('is-open');
            overlay?.classList.remove('is-open');
        };

        // Listen for panel section changes (show when wishlist widget is selected)
        if (window.elementor) {
            elementor.channels.editor.on('section:activated', (sectionName, editor) => {
                setTimeout(() => {
                    const widgetType = editor?.model?.get('widgetType');
                    if (widgetType === 'storedash-wishlist-icon') {
                        showEditorPanel();
                    } else {
                        hideEditorPanel();
                    }
                }, 100);
            });
        }

        // Open on widget click, close when clicking outside
        document.addEventListener('click', (e) => {
            const widget = e.target.closest('.elementor-widget-storedash-wishlist-icon');
            const sidebarClick = e.target.closest('.storedash-wishlist-sidebar');
            if (widget) {
                showEditorPanel();
            } else if (!sidebarClick) {
                hideEditorPanel();
            }
        });
    }

    // ─── Init ────────────────────────────────────────────────────────
    function init() {
        setupProductCardHeartCapture();
        setupWishlistGridRemove();
        updateAllBadges();
        syncAllHearts();
        bindAllHearts();

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                document.querySelector('.storedash-wishlist-sidebar')?.classList.add('transitions-ready');
                document.querySelector('.storedash-wishlist-overlay')?.classList.add('transitions-ready');
            });
        });

        // Handle Elementor editor mode
        if (isElementorEditor()) {
            handleElementorEditor();
            _editorHandled = true;
        }

        // Trigger clicks open panel
        document.querySelectorAll('.storedash-wishlist-trigger').forEach((trigger) => {
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                if (!isElementorEditor()) panel.open();
            });
            trigger.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (!isElementorEditor()) panel.open();
                }
            });
        });

        panel.init();

        // Re-bind hearts when new products are rendered
        document.addEventListener('storedash:products:rendered', () => {
            syncAllHearts();
            bindAllHearts();
        });

        // Observe DOM for dynamically added hearts
        const observer = new MutationObserver((mutations) => {
            let hasNew = false;
            for (const m of mutations) {
                for (const node of m.addedNodes) {
                    if (node.nodeType === 1 && (node.classList?.contains('product-wishlist-heart') || node.querySelector?.('.product-wishlist-heart'))) {
                        hasNew = true;
                        break;
                    }
                }
                if (hasNew) break;
            }
            if (hasNew) {
                syncAllHearts();
                bindAllHearts();
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Late-init for Elementor editor (elementorFrontend may not be ready at DOMContentLoaded)
    function initElementorWishlist() {
        if (typeof elementorFrontend !== 'undefined' && elementorFrontend.hooks) {
            elementorFrontend.hooks.addAction('frontend/element_ready/global', () => {
                if (!_editorHandled && isElementorEditor()) {
                    handleElementorEditor();
                    _editorHandled = true;
                }
            });
        }
    }

    if (typeof elementorFrontend !== 'undefined') {
        initElementorWishlist();
    } else {
        window.addEventListener('elementor/frontend/init', initElementorWishlist);
    }
})();
